// Generic CRUD engine entry page (see office-portal/design/CRUD_engine_*.md).
// Phase 1: navigation tree + read-only master grid.
// Phase 2: Add-blank/Add-copy, inline edit-and-save, delete (policy-aware), keyboard shortcuts.
// Phase 3: child-detail panel with tabs, a draggable divider, live sync on master-row focus,
// and many-to-many Link existing/Unlink - now in ChildDetailPanel.tsx (see Phase 5 note below).
// Phase 5: Undock - the child-detail panel can pop out into a separate, BroadcastChannel-synced
// browser window (generic_crud_detail_window.tsx). The panel itself was extracted into
// ../components/GenericCrud/ChildDetailPanel.tsx so both windows render the exact same component
// (REQ-DU-013/014) - see CRUD_engine_test_plan.md "Phase 5 - Undock & Sync".
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import type { CellFocusedEvent, CellKeyDownEvent, CellValueChangedEvent, ColDef, GridApi } from "ag-grid-community";
import type { GetContextMenuItemsParams } from "ag-grid-enterprise";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

import {
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FaArrowUpRightFromSquare, FaPlus, FaTag, FaTrash, FaXmark } from "react-icons/fa6";
import { useApi, type NavTreeNode } from "../hooks/useApi";
import BaseTemplate from "../components/BaseTemplate";
import InternalUserRoute from "../util/internal_user_route";
import { EDB_CUSTOM_withHeaderTooltips } from "../util/AgGridFunctions";
import { NotificationContext } from "../components/NotificationPane";
import type { ActionMeta, ChildMeta, EntityMeta, MetadataGraph } from "../schemas/crudMeta";
import type { NotificationContextType } from "../types";
import { type Row, buildColumnDefs, buildRowContextMenu, newTempId, buildSharedDefaultColDef } from "../components/GenericCrud/shared";
import { useColumnDefaults } from "../hooks/useColumnDefaults";
import ChildDetailPanel from "../components/GenericCrud/ChildDetailPanel";
import { publishWorkspaceContext } from "../lib/workspaceChannel";

function NavTree({ nodes, selected, onSelect }: { nodes: NavTreeNode[]; selected: string | null; onSelect: (entity: string) => void }) {
  return (
    <VStack align="stretch" spacing={0} pl={0}>
      {nodes.map((node) => (
        <Box key={node.ref}>
          <Box
            px={2}
            py={1}
            borderRadius="md"
            cursor={node.entity ? "pointer" : "default"}
            bg={node.entity === selected ? "blue.50" : undefined}
            fontWeight={node.entity ? "normal" : "semibold"}
            color={node.entity ? undefined : "gray.500"}
            onClick={() => node.entity && onSelect(node.entity)}
            _hover={node.entity ? { bg: "gray.100" } : undefined}
          >
            {node.label}
          </Box>
          {node.children.length > 0 && (
            <Box pl={4}>
              <NavTree nodes={node.children} selected={selected} onSelect={onSelect} />
            </Box>
          )}
        </Box>
      ))}
    </VStack>
  );
}

type GenericCrudProps = {
  navPath?: string; // reserved for deep-linking (URL-01), not yet wired to entity selection
};

export default function GenericCrud(_props: GenericCrudProps) {
  const {
    getGenericCrudMeta,
    getGenericCrudNav,
    getGenericCrudRows,
    putGenericCrudRow,
    deleteGenericCrudRow,
    getGenericCrudRowTags,
    addGenericCrudRowTag,
    removeGenericCrudRowTag,
  } = useApi();
  const columnDefaults = useColumnDefaults();
  const { pushNotification } = useContext<NotificationContextType | null>(NotificationContext)!;
  const gridApiRef = useRef<GridApi<Row> | null>(null);
  const splitContainerRef = useRef<HTMLDivElement>(null);
  // Set by the create-child custom action (CRA-01) right before it changes focusedRow/
  // activeChildTab: ChildDetailPanel's own loadChildRows effect fires from that state change and
  // appends this once its own fetch resolves, avoiding a race where the effect's fetch would
  // otherwise overwrite a locally-added blank row set synchronously in the same event handler.
  const pendingBlankChildRef = useRef<Row | null>(null);

  const [navTree, setNavTree] = useState<NavTreeNode[]>([]);
  const [metaGraph, setMetaGraph] = useState<MetadataGraph | null>(null);
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [filteredCount, setFilteredCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // Phase 3: child-detail panel state (LAY-01..08) - which row/tab is focused. The panel's own
  // internal data (childRows etc.) now lives inside ChildDetailPanel.
  const [focusedRow, setFocusedRow] = useState<Row | null>(null);
  const [activeChildTab, setActiveChildTab] = useState(0);
  const [masterHeightPct, setMasterHeightPct] = useState(60); // LAY-02 draggable divider position

  // Phase 4 slice: right-click context menu, custom row actions (CRA-01/02), tag action (CRA-08).
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [tagRowContext, setTagRowContext] = useState<{ entity: string; sk: number; label: string } | null>(null);
  const [rowTags, setRowTags] = useState<{ sk: number; tag_name: string }[]>([]);
  const [newTagName, setNewTagName] = useState("");

  // Phase 5: Undock - track whether a synced detail window is currently open, and detect when
  // the user closes it (REQ-DU-017/REQ-WIN-003).
  const detailWindowRef = useRef<Window | null>(null);
  const [isUndocked, setIsUndocked] = useState(false);
  const [childReloadNonce, setChildReloadNonce] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const [nav, meta] = await Promise.all([getGenericCrudNav(), getGenericCrudMeta()]);
        setNavTree(nav);
        setMetaGraph(meta);
        setErr(null);
      } catch (e: any) {
        setErr(e?.message || "Failed to load Generic CRUD metadata/navigation");
      } finally {
        setLoading(false);
      }
    })();
  }, [getGenericCrudNav, getGenericCrudMeta]);

  const loadRows = useCallback(
    async (entity: string) => {
      try {
        setLoading(true);
        const data = await getGenericCrudRows(entity, { limit: 200 });
        setRows(data.rows);
        setFilteredCount(data.filteredCount);
        setTotalCount(data.totalCount);
        setErr(null);
      } catch (e: any) {
        setErr(e?.message || "Failed to load rows");
      } finally {
        setLoading(false);
      }
    },
    [getGenericCrudRows],
  );

  const handleSelectEntity = useCallback(
    (entity: string) => {
      setSelectedEntity(entity);
      setFocusedRow(null);
      setActiveChildTab(0);
      loadRows(entity);
      publishWorkspaceContext({ selectedEntity: entity, focusedRowPk: null, focusedRowLabel: null, activeChildTab: 0 });
    },
    [loadRows],
  );

  const selectedEntityMeta = selectedEntity && metaGraph ? metaGraph.entities[selectedEntity] : null;
  const isEditable = !!selectedEntityMeta && selectedEntityMeta.editModeOptions !== "edit-form";
  const hasChildren = !!selectedEntityMeta && selectedEntityMeta.children.length > 0;
  const activeChild: ChildMeta | undefined = selectedEntityMeta?.children[activeChildTab];
  const activeChildMeta = activeChild && metaGraph ? metaGraph.entities[activeChild.childEntity] : null;

  // LAY-06: live-sync the detail panel to whichever master row is currently focused, including
  // via keyboard navigation, not only an explicit click. Also publishes the change so an undocked
  // detail window (if open) stays in sync (REQ-SYNC-001/009).
  const handleCellFocused = useCallback(
    (event: CellFocusedEvent<Row>) => {
      if (event.rowIndex == null) return;
      const data = gridApiRef.current?.getDisplayedRowAtIndex(event.rowIndex)?.data ?? null;
      setFocusedRow(data ?? null);
      if (selectedEntity && selectedEntityMeta) {
        publishWorkspaceContext({
          selectedEntity,
          focusedRowPk: data ? (data[selectedEntityMeta.pk] as number | string) : null,
          focusedRowLabel: data ? String(data[selectedEntityMeta.displayField]) : null,
          activeChildTab,
        });
      }
    },
    [selectedEntity, selectedEntityMeta, activeChildTab],
  );

  const handleActiveChildTabChange = useCallback(
    (idx: number) => {
      setActiveChildTab(idx);
      if (selectedEntity) {
        publishWorkspaceContext({
          selectedEntity,
          focusedRowPk: focusedRow && selectedEntityMeta ? (focusedRow[selectedEntityMeta.pk] as number | string) : null,
          focusedRowLabel: focusedRow && selectedEntityMeta ? String(focusedRow[selectedEntityMeta.displayField]) : null,
          activeChildTab: idx,
        });
      }
    },
    [selectedEntity, selectedEntityMeta, focusedRow],
  );

  // GRA-07: Add-blank/Add-copy insert a new row directly below whichever row is currently
  // focused (or at the top if nothing is focused yet), in either case fully editable and with
  // no server round-trip until the first field is actually edited (see handleCellValueChanged).
  const insertNewRow = useCallback(
    (template: Row | null) => {
      const base: Row = { __tempId: newTempId() };
      if (template && selectedEntityMeta) {
        for (const col of selectedEntityMeta.columns) base[col.field] = template[col.field] ?? null;
      }
      setRows((prev) => {
        const focusedRowIndex = gridApiRef.current?.getFocusedCell()?.rowIndex ?? -1;
        const insertAt = focusedRowIndex >= 0 ? focusedRowIndex + 1 : 0;
        const next = [...prev];
        next.splice(insertAt, 0, base);
        return next;
      });
    },
    [selectedEntityMeta],
  );

  const handleAddBlank = useCallback(() => insertNewRow(null), [insertNewRow]);

  const handleAddCopy = useCallback(() => {
    const focusedRowIndex = gridApiRef.current?.getFocusedCell()?.rowIndex;
    const sourceRow = focusedRowIndex != null ? gridApiRef.current?.getDisplayedRowAtIndex(focusedRowIndex)?.data : null;
    insertNewRow(sourceRow ?? null);
  }, [insertNewRow]);

  const handleDeleteRow = useCallback(
    async (row: Row) => {
      if (!selectedEntity || !selectedEntityMeta) return;
      // A never-saved new row has nothing server-side to delete yet.
      if (row.__tempId) {
        setRows((prev) => prev.filter((r) => r.__tempId !== row.__tempId));
        return;
      }
      const id = Number(row[selectedEntityMeta.pk]);
      if (!window.confirm(`Delete this ${selectedEntityMeta.labelSingular.toLowerCase()}?`)) return;
      try {
        const result = await deleteGenericCrudRow(selectedEntity, id);
        pushNotification(result.message || "Deleted.", "success");
        setRows((prev) => prev.filter((r) => r[selectedEntityMeta.pk] !== id));
      } catch (e: any) {
        // DEL-01: restrict refusals land here with a message naming the blocking relationship.
        pushNotification(e?.body?.error || e?.message || "Failed to delete row.", "error");
      }
    },
    [selectedEntity, selectedEntityMeta, deleteGenericCrudRow, pushNotification],
  );

  const handleCellValueChanged = useCallback(
    async (event: CellValueChangedEvent<Row>) => {
      if (!selectedEntity || !selectedEntityMeta || !event.data) return;
      const row = event.data;
      try {
        const result = await putGenericCrudRow(selectedEntity, row);
        setRows((prev) =>
          prev.map((r) =>
            r === row || (row.__tempId && r.__tempId === row.__tempId)
              ? { ...r, [selectedEntityMeta.pk]: result.sk, row_version: result.row_version, __tempId: undefined }
              : r,
          ),
        );
      } catch (e: any) {
        // INT-02/INT-04: a stale rowversion (409) or a failed validation (400) both roll back
        // by reloading from the server rather than leaving the grid showing an unsaved value.
        pushNotification(e?.body?.error || e?.message || "Failed to save row.", "error");
        await loadRows(selectedEntity);
      }
    },
    [selectedEntity, selectedEntityMeta, putGenericCrudRow, pushNotification, loadRows],
  );

  const handleCellKeyDown = useCallback(
    (event: CellKeyDownEvent<Row>) => {
      const native = event.event as KeyboardEvent | undefined;
      if (!native) return;
      // GRA-16: Insert/Alt+N add a row (Ctrl+N can't be used - browsers reserve it).
      if (native.key === "Insert" || (native.altKey && native.key.toLowerCase() === "n")) {
        native.preventDefault();
        handleAddBlank();
      } else if (native.key === "Delete" && event.data) {
        native.preventDefault();
        handleDeleteRow(event.data);
      }
    },
    [handleAddBlank, handleDeleteRow],
  );

  // LAY-02: drag the divider between the master grid and the detail panel to rescale the view.
  const handleDividerMouseDown = useCallback(() => {
    const onMove = (e: MouseEvent) => {
      const rect = splitContainerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const pct = ((e.clientY - rect.top) / rect.height) * 100;
      setMasterHeightPct(Math.min(85, Math.max(15, pct)));
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, []);

  // Phase 5 (REQ-MG-008..011, REQ-WIN-001/002): pop the detail panel out into a separate, named
  // window so re-clicking Undock focuses the existing window rather than opening a second one.
  const handleUndock = useCallback(() => {
    const w = window.open("/generic_crud/detail_window", "eyebright-detail-window", "width=900,height=700");
    if (w) {
      detailWindowRef.current = w;
      setIsUndocked(true);
      // Re-publish the current context immediately so the newly-opened window doesn't have to
      // wait for the next master-grid focus change to see what's currently selected.
      if (selectedEntity) {
        publishWorkspaceContext({
          selectedEntity,
          focusedRowPk: focusedRow && selectedEntityMeta ? (focusedRow[selectedEntityMeta.pk] as number | string) : null,
          focusedRowLabel: focusedRow && selectedEntityMeta ? String(focusedRow[selectedEntityMeta.displayField]) : null,
          activeChildTab,
        });
      }
    }
  }, [selectedEntity, selectedEntityMeta, focusedRow, activeChildTab]);

  // REQ-DU-017/REQ-WIN-003: detect the undocked window being closed by the user so Undock can be
  // clicked again to reopen it, rather than silently doing nothing against a dead reference.
  useEffect(() => {
    if (!isUndocked) return;
    const interval = setInterval(() => {
      if (detailWindowRef.current?.closed) {
        setIsUndocked(false);
        detailWindowRef.current = null;
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [isUndocked]);

  // CRA-01..10: dispatch a metadata-driven custom row action. `create-child` is a pure frontend
  // shortcut into an already-configured ui_child tab (only meaningful from the row's own entity's
  // master grid, since it switches that grid's child-tab state); `tag` opens the shared tag
  // picker and works from either the master grid or a child-detail grid.
  const handleRunAction = useCallback(
    (entityName: string, entityMeta: EntityMeta, action: ActionMeta, row: Row) => {
      if (action.actionType === "create-child") {
        const childEntity = (action.targetConfig as { childEntity?: string } | null)?.childEntity;
        const childIndex = entityMeta.children.findIndex((c) => c.childEntity === childEntity);
        const child = childIndex >= 0 ? entityMeta.children[childIndex] : undefined;
        if (entityName !== selectedEntity || !child || child.isManyToMany) {
          pushNotification("This action is only available from the main grid, for a non many-to-many child.", "info");
          return;
        }
        // Same deferred-save local insert as ChildDetailPanel's own "Add {child}" (LAY-05) - just
        // pre-targeted at this row's tab. pendingBlankChildRef hands the blank row down as a prop
        // so ChildDetailPanel's loadChildRows effect (triggered by the focusedRow/activeChildTab
        // change below) doesn't lose it to that fetch.
        const parentId = Number(row[entityMeta.pk]);
        pendingBlankChildRef.current = { __tempId: newTempId(), [child.fkColumn!]: parentId };
        setFocusedRow(row);
        setActiveChildTab(childIndex);
        setChildReloadNonce((n) => n + 1);
        publishWorkspaceContext({
          selectedEntity: entityName,
          focusedRowPk: row[entityMeta.pk] as number | string,
          focusedRowLabel: String(row[entityMeta.displayField]),
          activeChildTab: childIndex,
        });
        return;
      }
      if (action.actionType === "tag") {
        setTagRowContext({ entity: entityName, sk: Number(row[entityMeta.pk]), label: String(row[entityMeta.displayField]) });
        setTagModalOpen(true);
        return;
      }
      pushNotification(`Action type "${action.actionType}" isn't implemented yet.`, "info");
    },
    [selectedEntity, pushNotification],
  );

  const masterContextMenuItems = useCallback(
    (p: GetContextMenuItemsParams<Row>) =>
      selectedEntity && selectedEntityMeta
        ? buildRowContextMenu(selectedEntity, selectedEntityMeta, `Delete ${selectedEntityMeta.labelSingular.toLowerCase()}`, handleDeleteRow, handleRunAction)(p)
        : (p.defaultItems ?? []),
    [selectedEntity, selectedEntityMeta, handleDeleteRow, handleRunAction],
  );

  useEffect(() => {
    if (!tagModalOpen || !tagRowContext) return;
    (async () => {
      try {
        const data = await getGenericCrudRowTags(tagRowContext.entity, tagRowContext.sk);
        setRowTags(data.tags);
      } catch (e: any) {
        pushNotification(e?.body?.error || e?.message || "Failed to load tags.", "error");
      }
    })();
  }, [tagModalOpen, tagRowContext, getGenericCrudRowTags, pushNotification]);

  const handleAddTag = useCallback(async () => {
    if (!tagRowContext || !newTagName.trim()) return;
    try {
      await addGenericCrudRowTag(tagRowContext.entity, tagRowContext.sk, newTagName.trim());
      setNewTagName("");
      const data = await getGenericCrudRowTags(tagRowContext.entity, tagRowContext.sk);
      setRowTags(data.tags);
    } catch (e: any) {
      pushNotification(e?.body?.error || e?.message || "Failed to add tag.", "error");
    }
  }, [tagRowContext, newTagName, addGenericCrudRowTag, getGenericCrudRowTags, pushNotification]);

  const handleRemoveTag = useCallback(
    async (tagSk: number) => {
      if (!tagRowContext) return;
      try {
        await removeGenericCrudRowTag(tagRowContext.entity, tagRowContext.sk, tagSk);
        setRowTags((prev) => prev.filter((t) => t.sk !== tagSk));
      } catch (e: any) {
        pushNotification(e?.body?.error || e?.message || "Failed to remove tag.", "error");
      }
    },
    [tagRowContext, removeGenericCrudRowTag, pushNotification],
  );

  const defaultColDef = useMemo(() => buildSharedDefaultColDef(columnDefaults), [columnDefaults]);

  const columnDefs = useMemo<ColDef<Row>[]>(() => {
    if (!selectedEntityMeta) return [];
    const dataColumns = EDB_CUSTOM_withHeaderTooltips(buildColumnDefs(selectedEntityMeta, isEditable, columnDefaults));
    const actionsColumn: ColDef<Row> = {
      headerName: "",
      colId: "actions",
      pinned: "left",
      editable: false,
      filter: false,
      sortable: false,
      resizable: false,
      suppressHeaderMenuButton: true,
      width: 56,
      cellRenderer: (p: any) =>
        p.data ? (
          <IconButton aria-label="Delete row" icon={<FaTrash />} size="xs" variant="ghost" colorScheme="red" onClick={() => handleDeleteRow(p.data)} />
        ) : null,
    };
    return [actionsColumn, ...dataColumns];
  }, [selectedEntityMeta, isEditable, handleDeleteRow, columnDefaults]);

  return (
    <InternalUserRoute>
      <BaseTemplate fitToWindow>
        <BaseTemplate.Header>
          <HStack justifyContent="space-between">
            <Text fontWeight="semibold">Generic CRUD{selectedEntityMeta ? ` — ${selectedEntityMeta.label}` : ""}</Text>
            <HStack spacing={4}>
              {selectedEntityMeta && isEditable && (
                <HStack spacing={2}>
                  <Button size="sm" leftIcon={<FaPlus />} onClick={handleAddBlank}>
                    Add-blank
                  </Button>
                  <Button size="sm" leftIcon={<FaPlus />} onClick={handleAddCopy}>
                    Add-copy
                  </Button>
                </HStack>
              )}
              {selectedEntityMeta && (
                <Text fontSize="sm" color="gray.500">
                  page 1 · {filteredCount}/{totalCount}
                </Text>
              )}
            </HStack>
          </HStack>
        </BaseTemplate.Header>
        <BaseTemplate.Body>
          {err && <Text color="#b91c1c">{err}</Text>}
          <HStack align="stretch" spacing={4} h="100%">
            <Box minW="220px" borderRight="1px solid" borderColor="gray.200" pr={2}>
              <NavTree nodes={navTree} selected={selectedEntity} onSelect={handleSelectEntity} />
            </Box>
            <Box flex="1" h="100%">
              {!selectedEntity && <Text color="gray.500">Select an entity from the navigation tree to see its data.</Text>}
              {selectedEntity && !selectedEntityMeta && !loading && <Text color="#b91c1c">Unknown entity metadata: {selectedEntity}</Text>}
              {selectedEntityMeta && (
                <Box ref={splitContainerRef} w="100%" h="100%" display="flex" flexDirection="column">
                  {/* LAY-01: master grid and child-detail panel in separate divs. */}
                  <Box w="100%" h={hasChildren ? `${masterHeightPct}%` : "100%"} minH={0}>
                    <div className="ag-theme-quartz" style={{ width: "100%", height: "100%" }}>
                      <AgGridReact<Row>
                        rowData={rows}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        tooltipShowDelay={200}
                        getRowId={(params) => params.data.__tempId ?? String(params.data[selectedEntityMeta.pk])}
                        onGridReady={(e) => (gridApiRef.current = e.api)}
                        onCellValueChanged={handleCellValueChanged}
                        onCellKeyDown={handleCellKeyDown}
                        onCellFocused={handleCellFocused}
                        getContextMenuItems={masterContextMenuItems}
                        animateRows
                      />
                    </div>
                  </Box>

                  {hasChildren && (
                    <>
                      {/* LAY-02: draggable divider between the two panes. */}
                      <Box h="6px" cursor="row-resize" bg="gray.200" _hover={{ bg: "gray.300" }} onMouseDown={handleDividerMouseDown} flexShrink={0} />
                      <Box w="100%" flex="1" minH={0} overflow="auto" pt={2}>
                        <HStack justifyContent="flex-end" mb={1}>
                          <Button size="xs" variant="outline" leftIcon={<FaArrowUpRightFromSquare />} onClick={handleUndock}>
                            {isUndocked ? "Undocked (reopen)" : "Undock"}
                          </Button>
                        </HStack>
                        <ChildDetailPanel
                          selectedEntity={selectedEntity!}
                          selectedEntityMeta={selectedEntityMeta}
                          focusedRow={focusedRow}
                          activeChildTab={activeChildTab}
                          onActiveChildTabChange={handleActiveChildTabChange}
                          onRunAction={handleRunAction}
                          activeChildMeta={activeChildMeta}
                          pendingBlankChild={pendingBlankChildRef.current}
                          onPendingBlankChildConsumed={() => (pendingBlankChildRef.current = null)}
                          reloadNonce={childReloadNonce}
                        />
                      </Box>
                    </>
                  )}
                </Box>
              )}
            </Box>
          </HStack>
        </BaseTemplate.Body>
      </BaseTemplate>

      {/* CRA-08: tag picker - find-or-create by name, remove via the row_tag link only. */}
      <Modal isOpen={tagModalOpen} onClose={() => setTagModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Tags{tagRowContext ? ` — ${tagRowContext.label}` : ""}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            <HStack spacing={2} mb={3}>
              <Input
                placeholder="New tag name…"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddTag()}
              />
              <Button size="sm" leftIcon={<FaTag />} onClick={handleAddTag}>
                Add
              </Button>
            </HStack>
            <VStack align="stretch" spacing={1} maxH="300px" overflow="auto">
              {rowTags.map((t) => (
                <HStack key={t.sk} justifyContent="space-between" px={2} py={1} borderRadius="md" bg="gray.50">
                  <Text fontSize="sm">{t.tag_name}</Text>
                  <IconButton aria-label="Remove tag" icon={<FaXmark />} size="xs" variant="ghost" onClick={() => handleRemoveTag(t.sk)} />
                </HStack>
              ))}
              {rowTags.length === 0 && (
                <Text color="gray.500" fontSize="sm">
                  No tags yet.
                </Text>
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </InternalUserRoute>
  );
}
